import React, { useState, useRef } from "react";
import AdminModal from "../../Common/AdminModal";
import {
  downloadCsvTemplate,
  downloadExcelTemplate,
  parseQuestionsSpreadsheet,
} from "../../../../utils/questionSpreadsheetParser";
import { Download, Upload, CheckCircle2, AlertCircle, FileSpreadsheet } from "lucide-react";

export default function BulkUploadModal({
  isOpen,
  onClose,
  onUploadSuccess,
}) {
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isParsing, setIsParsing] = useState(false);
  const [parseResult, setParseResult] = useState(null);
  const [parseError, setParseError] = useState("");

  const resetState = () => {
    setSelectedFile(null);
    setIsParsing(false);
    setParseResult(null);
    setParseError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setSelectedFile(file);
    setIsParsing(true);
    setParseError("");
    setParseResult(null);

    try {
      const result = await parseQuestionsSpreadsheet(file);
      setParseResult(result);
    } catch (err) {
      setParseError(err.message || "Failed to parse file.");
    } finally {
      setIsParsing(false);
    }
  };

  const handleConfirmUpload = () => {
    if (!parseResult || !parseResult.validQuestions.length) return;
    onUploadSuccess(parseResult.validQuestions);
    resetState();
    onClose();
  };

  return (
    <AdminModal
      isOpen={isOpen}
      onClose={() => {
        resetState();
        onClose();
      }}
      title="Bulk Upload Questions (CSV / Excel)"
      maxWidth="760px"
      footer={
        <>
          <button
            type="button"
            className="admin-btn admin-btn-secondary"
            onClick={() => {
              resetState();
              onClose();
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            className="admin-btn admin-btn-primary"
            disabled={!parseResult || parseResult.validQuestions.length === 0}
            onClick={handleConfirmUpload}
          >
            <Upload size={16} />
            Upload {parseResult ? `${parseResult.validQuestions.length} Questions` : ""}
          </button>
        </>
      }
    >
      <div>
        {/* Step 1: Download Templates */}
        <div
          style={{
            background: "#f8fafc",
            border: "1px solid var(--admin-border)",
            borderRadius: "8px",
            padding: "16px 20px",
            marginBottom: "20px",
          }}
        >
          <h4 style={{ fontSize: "14px", fontWeight: 700, margin: "0 0 6px 0", color: "#1e293b" }}>
            Step 1: Download Official Template
          </h4>
          <p style={{ fontSize: "13px", color: "#64748b", margin: "0 0 12px 0" }}>
            Download the structured template with sample questions, fill in your questions, and upload below.
          </p>
          <div style={{ display: "flex", gap: "10px" }}>
            <button
              type="button"
              className="admin-btn admin-btn-secondary admin-btn-sm"
              onClick={downloadExcelTemplate}
            >
              <Download size={14} /> Download Excel Template (.xlsx)
            </button>
            <button
              type="button"
              className="admin-btn admin-btn-secondary admin-btn-sm"
              onClick={downloadCsvTemplate}
            >
              <Download size={14} /> Download CSV Template (.csv)
            </button>
          </div>
        </div>

        {/* Step 2: Upload File */}
        <div style={{ marginBottom: "20px" }}>
          <h4 style={{ fontSize: "14px", fontWeight: 700, margin: "0 0 8px 0", color: "#1e293b" }}>
            Step 2: Upload Filled File
          </h4>
          <div
            style={{
              border: "2px dashed #cbd5e1",
              borderRadius: "10px",
              padding: "28px",
              textAlign: "center",
              cursor: "pointer",
              background: "#fafafa",
              transition: "border-color 0.2s ease",
            }}
            onClick={() => fileInputRef.current?.click()}
          >
            <FileSpreadsheet size={36} style={{ color: "#0284c7", marginBottom: "8px" }} />
            <p style={{ margin: "0 0 4px 0", fontWeight: 600, fontSize: "14px", color: "#334155" }}>
              {selectedFile ? selectedFile.name : "Click to select or drop CSV or Excel file"}
            </p>
            <span style={{ fontSize: "12px", color: "#94a3b8" }}>
              Supports .xlsx, .xls, and .csv
            </span>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv, .xlsx, .xls"
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
          </div>
        </div>

        {/* Parsing Indicator */}
        {isParsing && (
          <div style={{ textAlign: "center", padding: "16px", color: "#0284c7", fontSize: "14px" }}>
            Validating questions spreadsheet...
          </div>
        )}

        {/* Parse Error */}
        {parseError && (
          <div
            style={{
              background: "#fee2e2",
              border: "1px solid #fecaca",
              padding: "12px 16px",
              borderRadius: "8px",
              color: "#b91c1c",
              fontSize: "13px",
              marginBottom: "16px",
            }}
          >
            {parseError}
          </div>
        )}

        {/* Parse Result Summary */}
        {parseResult && (
          <div>
            <div style={{ display: "flex", gap: "16px", marginBottom: "16px" }}>
              <div
                style={{
                  flex: 1,
                  background: "#f0fdf4",
                  border: "1px solid #bbf7d0",
                  padding: "12px 16px",
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                <CheckCircle2 size={24} style={{ color: "#16a34a" }} />
                <div>
                  <div style={{ fontSize: "16px", fontWeight: 700, color: "#16a34a" }}>
                    {parseResult.validQuestions.length}
                  </div>
                  <div style={{ fontSize: "12px", color: "#15803d" }}>Valid Questions Ready</div>
                </div>
              </div>

              {parseResult.errors.length > 0 && (
                <div
                  style={{
                    flex: 1,
                    background: "#fef2f2",
                    border: "1px solid #fecaca",
                    padding: "12px 16px",
                    borderRadius: "8px",
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                  }}
                >
                  <AlertCircle size={24} style={{ color: "#dc2626" }} />
                  <div>
                    <div style={{ fontSize: "16px", fontWeight: 700, color: "#dc2626" }}>
                      {parseResult.errors.length}
                    </div>
                    <div style={{ fontSize: "12px", color: "#b91c1c" }}>Rows with Issues (Skipped)</div>
                  </div>
                </div>
              )}
            </div>

            {/* Error Rows Table if any */}
            {parseResult.errors.length > 0 && (
              <div
                style={{
                  maxHeight: "160px",
                  overflowY: "auto",
                  border: "1px solid #fecaca",
                  borderRadius: "6px",
                  background: "#fff",
                }}
              >
                <table style={{ width: "100%", fontSize: "12px", borderCollapse: "collapse" }}>
                  <thead style={{ background: "#fef2f2", color: "#991b1b" }}>
                    <tr>
                      <th style={{ padding: "6px 10px", textAlign: "left", width: "60px" }}>Row</th>
                      <th style={{ padding: "6px 10px", textAlign: "left" }}>Issues</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parseResult.errors.map((err, i) => (
                      <tr key={i} style={{ borderBottom: "1px solid #fecaca" }}>
                        <td style={{ padding: "6px 10px", fontWeight: 600, color: "#b91c1c" }}>
                          {err.row}
                        </td>
                        <td style={{ padding: "6px 10px", color: "#7f1d1d" }}>{err.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </AdminModal>
  );
}
